/**
 * Image formats the server accepts for uploads.
 *
 * The API validates images with Pillow (see `RemovableImageField` in
 * disco-backend), so vector formats (.svg) and formats Pillow has no decoder
 * for (.heic/.heif from iOS devices) are rejected with a 400 even though the
 * browser is happy to preview them.
 */
const SUPPORTED_IMAGE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'bmp',
  'tif',
  'tiff',
]

const SUPPORTED_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/x-ms-bmp',
  'image/tiff',
]

/**
 * Value for the `accept` attribute of an image `<input type="file">`.
 *
 * Keeps unsupported formats out of the native file picker. Extensions are
 * listed alongside the MIME types because some platforms report an empty
 * `type` for less common formats.
 */
export const SUPPORTED_IMAGE_ACCEPT: string = [
  ...SUPPORTED_IMAGE_MIME_TYPES,
  ...SUPPORTED_IMAGE_EXTENSIONS.map(extension => `.${extension}`),
].join(',')

const SUPPORTED_FORMATS_TEXT = 'JPG, PNG, GIF, WEBP, BMP or TIFF'

const getExtension = (filename?: string): string =>
  (filename || '').split('.').pop()?.toLowerCase() ?? ''

const getFormatLabel = (file: File): string => {
  const extension = getExtension(file.name)
  if (extension && extension !== file.name.toLowerCase()) {
    return extension.toUpperCase()
  }

  const subtype = file.type.split('/')[1]
  return subtype ? subtype.split('+')[0]?.toUpperCase() ?? '' : ''
}

/**
 * Checks whether an image file is in a format the server can process.
 *
 * Accepts the file if either its extension or its MIME type is known to be
 * supported, so an unrecognised `type` from the browser doesn't block an
 * otherwise valid upload.
 *
 * @param file - Image file selected by the user
 * @returns True when the format is supported
 *
 * @example
 * ```typescript
 * isSupportedImageFormat(new File([], 'cover.png', { type: 'image/png' })) // true
 * isSupportedImageFormat(new File([], 'logo.svg', { type: 'image/svg+xml' })) // false
 * ```
 */
export const isSupportedImageFormat = (file: File): boolean =>
  SUPPORTED_IMAGE_EXTENSIONS.includes(getExtension(file.name)) ||
  SUPPORTED_IMAGE_MIME_TYPES.includes(file.type.toLowerCase())

/**
 * Builds the error message shown when an image format is not supported.
 *
 * Names the rejected format when it can be determined, so the user knows what
 * to change instead of retrying the same file.
 *
 * @param file - Image file selected by the user
 * @returns Message naming the unsupported format and the supported ones
 *
 * @example
 * ```typescript
 * unsupportedImageFormatMessage(new File([], 'logo.svg'))
 * // 'SVG images are not supported. Please use a JPG, PNG, GIF, WEBP, BMP or TIFF image.'
 * ```
 */
export const unsupportedImageFormatMessage = (file: File): string => {
  const label = getFormatLabel(file)
  const subject = label ? `${label} images are` : 'That image format is'
  return `${subject} not supported. Please use a ${SUPPORTED_FORMATS_TEXT} image.`
}
